import { formatUren, formatUrenMetTeken } from '@/lib/format'
import type { InzetStatus } from '@/lib/medewerker-types'
import { cn } from '@/lib/utils'

const VULLING: Record<InzetStatus, string> = {
  overbezet: 'bg-status-overbezet/45',
  onderbezet: 'bg-status-onderbezet/55',
  in_orde: 'bg-status-in-orde/60',
  aanstelling_onbekend: 'bg-status-onbekend/30',
}

const BALANS_TEKST: Record<InzetStatus, string> = {
  overbezet: 'text-status-overbezet',
  onderbezet: 'text-status-onderbezet',
  in_orde: 'text-ink-soft',
  aanstelling_onbekend: 'text-ink-faint',
}

interface BalansBalkProps {
  toegewezen: number
  beschikbaar: number | null
  status: InzetStatus
  className?: string
}

function samenvatting(toegewezen: number, beschikbaar: number | null): string {
  if (beschikbaar === null) {
    return `${formatUren(toegewezen)} uur toegewezen, aanstelling onbekend`
  }
  const verschil = beschikbaar - toegewezen
  if (verschil === 0) return `${formatUren(toegewezen)} van ${formatUren(beschikbaar)} uur, precies vol`
  if (verschil > 0) {
    return `${formatUren(toegewezen)} van ${formatUren(beschikbaar)} uur, ${formatUren(verschil)} uur ruimte`
  }
  return `${formatUren(toegewezen)} van ${formatUren(beschikbaar)} uur, ${formatUren(-verschil)} uur te veel`
}

export function BalansBalk({ toegewezen, beschikbaar, status, className }: BalansBalkProps) {
  const schaal = Math.max(toegewezen, beschikbaar ?? 0, 1)
  const binnenGrens = beschikbaar === null ? toegewezen : Math.min(toegewezen, beschikbaar)
  const overGrens = beschikbaar === null ? 0 : Math.max(0, toegewezen - beschikbaar)
  const grensPositie = beschikbaar === null ? null : (beschikbaar / schaal) * 100

  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      <div
        role="img"
        aria-label={samenvatting(toegewezen, beschikbaar)}
        className="relative h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-fill-2"
      >
        <div className="absolute inset-y-0 left-0 flex w-full origin-left animate-grow-bar-x">
          <div
            className={cn('h-full', VULLING[status])}
            style={{ width: `${(binnenGrens / schaal) * 100}%` }}
          />
          {overGrens > 0 ? (
            <div
              className="h-full bg-status-overbezet"
              style={{ width: `${(overGrens / schaal) * 100}%` }}
            />
          ) : null}
        </div>
        {grensPositie !== null && overGrens > 0 ? (
          <div
            className="absolute inset-y-0 w-0.5 bg-canvas"
            style={{ left: `calc(${grensPositie}% - 1px)` }}
          />
        ) : null}
      </div>

      <div className="flex shrink-0 items-baseline gap-3 text-sm">
        <span className="num w-[7.5rem] text-ink-soft">
          <span className="font-medium text-ink">{formatUren(toegewezen)}</span>
          {beschikbaar === null ? (
            <span className="text-ink-faint"> van onbekend</span>
          ) : (
            <span className="text-ink-faint"> van {formatUren(beschikbaar)} u</span>
          )}
        </span>
        <span className={cn('num w-14 text-sm font-medium', BALANS_TEKST[status])}>
          {beschikbaar === null ? '-' : formatUrenMetTeken(beschikbaar - toegewezen)}
        </span>
      </div>
    </div>
  )
}
